/**
 * The idle gate — the single source of truth for "is the user engaged right now".
 *
 * Engaged means: the tab is visible AND there has been interaction within
 * TELEMETRY_IDLE_GATE_MS. Everything that measures engaged time reads from here,
 * so the definition lives in exactly one place.
 *
 * Transitions are driven by a scheduled timer rather than polling, so the moment
 * the gate closes is exact rather than rounded to a poll interval — which
 * matters because that moment is subtracted from every engaged-time figure.
 */

import {
  TELEMETRY_ACTIVITY_EVENTS,
  TELEMETRY_ACTIVITY_THROTTLE_MS,
  TELEMETRY_IDLE_GATE_MS,
} from "src/constants/telemetry";

type Listener = (engaged: boolean) => void;

const listeners = new Set<Listener>();
let lastActivityAt = Date.now();
let idleTimer: number | undefined;
let engaged = false;
let started = false;

const isVisible = (): boolean =>
  typeof document === "undefined" || document.visibilityState === "visible";

const setEngaged = (next: boolean): void => {
  if (next === engaged) return;
  engaged = next;
  // A listener throwing must not stop the others, or one bad consumer would
  // freeze every timer in the app.
  listeners.forEach((fn) => {
    try {
      fn(engaged);
    } catch {
      /* telemetry must never break the page */
    }
  });
};

const scheduleIdle = (): void => {
  window.clearTimeout(idleTimer);
  const elapsed = Date.now() - lastActivityAt;
  const remaining = TELEMETRY_IDLE_GATE_MS - elapsed;
  if (remaining <= 0) {
    setEngaged(false);
    return;
  }
  idleTimer = window.setTimeout(() => setEngaged(false), remaining);
};

const onActivity = (): void => {
  const now = Date.now();
  // Throttle: mousemove alone would call this ~100×/sec.
  if (engaged && now - lastActivityAt < TELEMETRY_ACTIVITY_THROTTLE_MS) return;
  lastActivityAt = now;
  if (isVisible()) setEngaged(true);
  scheduleIdle();
};

const onVisibilityChange = (): void => {
  if (isVisible()) {
    // Returning to the tab resumes the SAME session — it is not a new visit.
    lastActivityAt = Date.now();
    setEngaged(true);
    scheduleIdle();
  } else {
    // A hidden tab is never engaged, regardless of the idle timer.
    window.clearTimeout(idleTimer);
    setEngaged(false);
  }
};

export const idleGate = {
  /** Begin listening. Idempotent. */
  start(): void {
    if (started || typeof window === "undefined") return;
    started = true;
    TELEMETRY_ACTIVITY_EVENTS.forEach((evt) =>
      // passive: these listeners never preventDefault, and marking them so keeps
      // scroll/touch handling off the main thread's critical path.
      window.addEventListener(evt, onActivity, { passive: true }),
    );
    document.addEventListener("visibilitychange", onVisibilityChange);
    lastActivityAt = Date.now();
    setEngaged(isVisible());
    scheduleIdle();
  },

  stop(): void {
    if (!started) return;
    started = false;
    TELEMETRY_ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearTimeout(idleTimer);
    setEngaged(false);
  },

  isEngaged: (): boolean => engaged,

  /** Subscribe to transitions. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/**
 * Accumulates engaged milliseconds for one scope (a session, a dashboard visit).
 *
 * Created per scope rather than shared, because "time on this dashboard" and
 * "time in this session" advance together but reset independently.
 */
export class EngagedTimer {
  private accumulated = 0;
  private openedAt: number | null = null;
  private readonly unsubscribe: () => void;

  constructor() {
    if (idleGate.isEngaged()) this.openedAt = Date.now();
    this.unsubscribe = idleGate.subscribe((isEngaged) => {
      if (isEngaged) {
        this.openedAt ??= Date.now();
      } else {
        this.closeInterval();
      }
    });
  }

  private closeInterval(): void {
    if (this.openedAt === null) return;
    this.accumulated += Date.now() - this.openedAt;
    this.openedAt = null;
  }

  /** Engaged ms so far, including any interval still open. */
  read(): number {
    const open = this.openedAt === null ? 0 : Date.now() - this.openedAt;
    return Math.round(this.accumulated + open);
  }

  /** Read and zero — used to report a delta without double-counting it later. */
  drain(): number {
    const value = this.read();
    this.accumulated = 0;
    this.openedAt = idleGate.isEngaged() ? Date.now() : null;
    return value;
  }

  /**
   * Give a drained delta back after a send that did not happen.
   *
   * `drain()` has to run BEFORE the request, because the payload needs the value.
   * If the request then never goes out — no access token during a refresh window,
   * or the fetch rejects — those milliseconds were previously just gone, silently
   * under-reporting engaged time. Crediting them back means the next successful
   * flush carries them instead.
   */
  credit(ms: number): void {
    if (ms > 0) this.accumulated += ms;
  }

  dispose(): void {
    this.closeInterval();
    this.unsubscribe();
  }
}
